// The engagement schedule personnel contracts are merged from, on the Event
// dialog (Contracts.personnel): the Word template (uploaded, or copied from an
// earlier event), the event's location, and the Role fields — one event-wide
// default, overridden field by field for any role that differs.
//
// The template saves as soon as it's uploaded; the text fields have their own
// Save, since they're typed.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Checkbox, CircularProgress, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Paper, Stack,
  TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { ATTENDANCE_ROLES } from "../constants";
import { CONTRACTS_API, fmtDateTime } from "../contracts";
import { downloadBlob } from "../download";
import { useContractConfig } from "../useContractConfig";
import { useWriteLock } from "../useWriteLock";
import { notify } from "../notify";

const ROLE_FIELDS = [
  { key: "briefing", label: "Briefing", field: "RoleBriefing" },
  { key: "departure", label: "Departure", field: "RoleDeparture" },
  { key: "logistics", label: "Logistics — travel, accommodation, catering", field: "RoleLogistics" },
];

const toRows = (details) => {
  const def = details.find(d => d.role == null) || { role: null };
  const overrides = details.filter(d => d.role != null);
  return [def, ...overrides].map(d => ({
    role: d.role ?? null, briefing: d.briefing || "", departure: d.departure || "", logistics: d.logistics || "",
  }));
};

/**
 * Props: eventId (a number once the event exists), onSummary(text) — a one-line
 * description for a collapsed section.
 */
export default function EngagementManager({ eventId, onSummary }) {
  const config = useContractConfig();
  const identified = typeof eventId === "number";
  const [data, setData] = useState(null);
  const [location, setLocation] = useState("");
  const [rows, setRows] = useState([]);
  const [initial, setInitial] = useState("");
  const [uploading, setUploading] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFrom, setCopyFrom] = useState("");
  const [copyRoles, setCopyRoles] = useState(true);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [addRole, setAddRole] = useState("");
  const fileInput = useRef(null);
  const { busy, run } = useWriteLock();

  const apply = useCallback((d) => {
    setData(d);
    const r = toRows(d.role_details || []);
    setRows(r);
    setLocation(d.location || "");
    setInitial(JSON.stringify({ location: d.location || "", rows: r }));
  }, []);

  useEffect(() => {
    if (!identified) { setData(null); return; }
    axios.get(`${CONTRACTS_API}/engagement/${eventId}`).then(res => apply(res.data)).catch(() => {});
  }, [identified, eventId, apply]);

  useEffect(() => {
    if (!data) return;
    const overrides = (data.role_details || []).filter(d => d.role != null).length;
    onSummary?.(data.template_filename
      ? [`Template: ${data.template_filename}`,
         data.location ? null : "no location set",
         overrides ? `${overrides} event role override${overrides === 1 ? "" : "s"}` : null,
        ].filter(Boolean).join(" · ")
      : "No template yet");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!identified) return null;
  if (!data) return <Stack sx={{ alignItems: "center", py: 2 }}><CircularProgress size={24} /></Stack>;

  const dirty = JSON.stringify({ location, rows }) !== initial;
  const used = new Set(data.template_fields || []);
  const usedRoleFields = ROLE_FIELDS.filter(f => !data.template_filename || used.has(f.field));

  const upload = (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    setUploading(true);
    axios.post(`${CONTRACTS_API}/engagement/${eventId}/template`, body)
      .then(res => {
        apply(res.data);
        notify(res.data.template_unknown_fields?.length
          ? `Template saved — but nothing fills ${res.data.template_unknown_fields.map(f => `{{${f}}}`).join(", ")}.`
          : "Template saved.", res.data.template_unknown_fields?.length ? "warning" : "success");
      })
      .catch(() => {})
      .finally(() => { setUploading(false); if (fileInput.current) fileInput.current.value = ""; });
  };

  const download = () => axios.get(`${CONTRACTS_API}/engagement/${eventId}/template/file`, { responseType: "blob" })
    .then(res => downloadBlob(res.data, data.template_filename || "engagement-schedule.docx")).catch(() => {});

  const remove = () => run(() => axios.delete(`${CONTRACTS_API}/engagement/${eventId}/template`)
    .then(res => { apply(res.data); notify("Template removed.", "success"); }));

  const copy = () => run(() => axios.post(`${CONTRACTS_API}/engagement/${eventId}/template/copy`, {
    from_event_id: Number(copyFrom), include_role_details: copyRoles,
  }).then(res => { apply(res.data); setCopyOpen(false); notify("Template copied.", "success"); }));

  const save = () => run(() => axios.put(`${CONTRACTS_API}/engagement/${eventId}`, {
    location: location.trim() || null,
    role_details: rows.map(r => ({ ...r })),
  }).then(res => { apply(res.data); notify("Engagement details saved.", "success"); }));

  const setRow = (i, key) => (e) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r)));
  const overridden = new Set(rows.filter(r => r.role).map(r => r.role));

  return (
    <Stack spacing={1.5}>
      {/* The template */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1}>
          {data.template_filename ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <DescriptionIcon fontSize="small" color="action" />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }}>
                {data.template_filename}
                <Typography component="span" variant="caption" color="text.secondary">
                  {" "}— uploaded {fmtDateTime(data.template_uploaded_at)}
                </Typography>
              </Typography>
              <Button size="small" onClick={download}>Download</Button>
              <Tooltip title="Remove template">
                <IconButton size="small" color="error" disabled={busy} onClick={remove} aria-label="Remove template">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No engagement schedule template yet — upload one, or copy an earlier event's.
            </Typography>
          )}
          {data.template_problem && <Alert severity="error">{data.template_problem}</Alert>}
          {data.template_unknown_fields?.length > 0 && (
            <Alert severity="warning">
              Nothing fills {data.template_unknown_fields.map(f => `{{${f}}}`).join(", ")} — check the spelling
              against the list of fields.
            </Alert>
          )}
          {data.template_filename && !data.template_signature_fields?.length && (
            <Alert severity="info">
              The template has no signature placeholders, so signatures will appear only on the signing
              certificate at the end.
            </Alert>
          )}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <input type="file" hidden ref={fileInput}
                   accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                   onChange={e => upload(e.target.files?.[0])} />
            <Button size="small" variant="outlined" disabled={uploading}
                    startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                    onClick={() => fileInput.current?.click()}>
              {data.template_filename ? "Replace template" : "Upload template"}
            </Button>
            {data.copy_sources.length > 0 && (
              <Button size="small" startIcon={<ContentCopyIcon />}
                      onClick={() => { setCopyFrom(String(data.copy_sources[0].event_id)); setCopyOpen(true); }}>
                Copy from another event
              </Button>
            )}
            <Button size="small" onClick={() => setFieldsOpen(o => !o)}
                    endIcon={fieldsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
              Fields you can use
            </Button>
          </Box>
          <Collapse in={fieldsOpen} unmountOnExit>
            <Stack spacing={0.25} sx={{ pl: 1 }}>
              {[...(config?.fields || []), ...(config?.signature_fields || [])
                .filter(f => !f.name.startsWith("Client") && !f.name.startsWith("Counterparty"))].map(f => (
                <Typography key={f.name} variant="caption" component="div">
                  <Box component="code" sx={{ fontWeight: 600 }}>{`{{${f.name}}}`}</Box>
                  {used.has(f.name) || (data.template_signature_fields || []).includes(f.name) ? " ✓" : ""}
                  {" "}— {f.description}
                </Typography>
              ))}
            </Stack>
          </Collapse>
        </Stack>
      </Paper>

      {/* Location and Role fields */}
      <TextField label="Event location" fullWidth size="small" value={location}
                 onChange={e => setLocation(e.target.value)}
                 helperText="{{EventLocation}} — e.g. St Bees to Robin Hood's Bay." />
      {rows.map((r, i) => (
        <Paper key={r.role ?? "__default"} variant="outlined" sx={{ p: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {r.role ? `${r.role} — overrides` : "All event roles (default)"}
            </Typography>
            {r.role && (
              <Tooltip title="Remove override">
                <IconButton size="small" aria-label={`Remove ${r.role} override`}
                            onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Stack spacing={1.5}>
            {usedRoleFields.map(f => (
              <TextField key={f.key} label={f.label} fullWidth size="small" multiline minRows={f.key === "logistics" ? 2 : 1}
                         value={r[f.key]} onChange={setRow(i, f.key)}
                         helperText={r.role ? "Leave blank to use the default." : `{{${f.field}}}`} />
            ))}
          </Stack>
        </Paper>
      ))}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField select size="small" label="Override for an event role" value={addRole} sx={{ minWidth: 220 }}
                   onChange={e => {
                     const role = e.target.value;
                     if (role) setRows(rs => [...rs, { role, briefing: "", departure: "", logistics: "" }]);
                     setAddRole("");
                   }}>
          <MenuItem value=""><em>Choose an event role…</em></MenuItem>
          {ATTENDANCE_ROLES.filter(role => !overridden.has(role)).map(role => (
            <MenuItem key={role} value={role}>{role}</MenuItem>
          ))}
        </TextField>
        <AddIcon fontSize="small" color="action" />
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" size="small" disabled={!dirty || busy} onClick={save}>
          Save engagement details
        </Button>
      </Box>

      {/* Copy from another event */}
      <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Copy a template</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField select label="From event" fullWidth value={copyFrom} onChange={e => setCopyFrom(e.target.value)}>
              {data.copy_sources.map(s => <MenuItem key={s.event_id} value={String(s.event_id)}>{s.label}</MenuItem>)}
            </TextField>
            <FormControlLabel control={<Checkbox checked={copyRoles} onChange={e => setCopyRoles(e.target.checked)} />}
                              label="Copy its briefing, departure and logistics too (replacing these)" />
            {data.template_filename && (
              <Alert severity="info">This replaces this event's current template.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!copyFrom || busy} onClick={copy}>Copy</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
